import axios from 'axios';

// TMDB API Configuration
const TMDB_API_CONFIG = {
  BASE_URL: 'https://api.themoviedb.org/3',
  IMAGE_BASE_URL: 'https://image.tmdb.org/t/p/',
  API_KEY: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJjNmU5MzM1Yjg5Y2E3NWE3MGJjY2UxYzcyYmZkMDQ4ZCIsInN1YiI6IjYzYmVkN2FiODU4Njc4MDBmMDhjZjI3NiIsInNjb3BlcyI6WyJhcGlfcmVhZCJdLCJ2ZXJzaW9uIjoxfQ.sQHes_rn51wewxY_7nZLxGssnd67J8ieiLOIo2Bg_FI'
};

// Default axios instance for TMDB API
const tmdbClient = axios.create({
  baseURL: TMDB_API_CONFIG.BASE_URL,
  timeout: 10000,
  headers: {
    'accept': 'application/json',
    'Authorization': TMDB_API_CONFIG.API_KEY
  }
});

// Utility functions
export const formatImageUrl = (path, size = 'w500') => {
  if (!path) return null;
  return `${TMDB_API_CONFIG.IMAGE_BASE_URL}${size}${path}`;
};

export const formatBackdropUrl = (path, size = 'original') => {
  if (!path) return null;
  return `${TMDB_API_CONFIG.IMAGE_BASE_URL}${size}${path}`;
};

export const formatMovieData = (item) => ({
  id: item.id,
  title: item.title || item.name,
  original_title: item.original_title || item.original_name,
  overview: item.overview,
  poster_path: item.poster_path,
  backdrop_path: item.backdrop_path,
  vote_average: item.vote_average,
  vote_count: item.vote_count,
  release_date: item.release_date || item.first_air_date,
  media_type: item.media_type || (item.title ? 'movie' : 'tv'),
  adult: item.adult,
  genre_ids: item.genre_ids,
  popularity: item.popularity,
  video: item.video,
  original_language: item.original_language,
  // Formatted data for UI
  imgSrc: formatImageUrl(item.poster_path),
  backdropSrc: formatBackdropUrl(item.backdrop_path),
  year: item.release_date || item.first_air_date ? 
    new Date(item.release_date || item.first_air_date).getFullYear() : 'N/A',
  duration: item.media_type === 'tv' ? 'Series' : 'Movie',
  ageRating: item.adult ? '18+' : '13+',
  link: `/${item.media_type || (item.title ? 'movie' : 'tv')}-details/${item.id}`
});

// Main TMDB API Service Class
class TMDBAPIService {
  // Search functions
  async searchMulti(query, options = {}) {
    try {
      const response = await tmdbClient.get('/search/multi', {
        params: {
          query,
          include_adult: false,
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error searching multi:', error);
      throw error;
    }
  }

  async searchMovies(query, options = {}) {
    try {
      const response = await tmdbClient.get('/search/movie', {
        params: {
          query,
          include_adult: false,
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error searching movies:', error);
      throw error;
    }
  }

  async searchTVShows(query, options = {}) {
    try {
      const response = await tmdbClient.get('/search/tv', {
        params: {
          query,
          include_adult: false,
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error searching TV shows:', error);
      throw error;
    }
  }

  async searchPeople(query, options = {}) {
    try {
      const response = await tmdbClient.get('/search/person', {
        params: {
          query,
          include_adult: false,
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error searching people:', error);
      throw error;
    }
  }

  // Get trending content
  async getTrending(timeWindow = 'week', options = {}) {
    try {
      const response = await tmdbClient.get(`/trending/all/${timeWindow}`, {
        params: {
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting trending:', error);
      throw error;
    }
  }

  // Get popular content
  async getPopularMovies(options = {}) {
    try {
      const response = await tmdbClient.get('/movie/popular', {
        params: {
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting popular movies:', error);
      throw error;
    }
  }

  async getPopularTVShows(options = {}) {
    try {
      const response = await tmdbClient.get('/tv/popular', {
        params: {
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting popular TV shows:', error);
      throw error;
    }
  }

  // Get top rated content
  async getTopRatedMovies(options = {}) {
    try {
      const response = await tmdbClient.get('/movie/top_rated', {
        params: {
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting top rated movies:', error);
      throw error;
    }
  }

  async getTopRatedTVShows(options = {}) {
    try {
      const response = await tmdbClient.get('/tv/top_rated', {
        params: {
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting top rated TV shows:', error);
      throw error;
    }
  }

  // Get upcoming movies
  async getUpcomingMovies(options = {}) {
    try {
      const response = await tmdbClient.get('/movie/upcoming', {
        params: {
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting upcoming movies:', error);
      throw error;
    }
  }

  // Get now playing movies
  async getNowPlayingMovies(options = {}) {
    try {
      const response = await tmdbClient.get('/movie/now_playing', {
        params: {
          language: 'en-US',
          page: options.page || 1,
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting now playing movies:', error);
      throw error;
    }
  }

  // Get latest movies
  async getLatestMovies(options = {}) {
    try {
      const response = await tmdbClient.get('/movie/latest', {
        params: {
          language: 'en-US',
          ...options
        }
      });
      return formatMovieData(response.data);
    } catch (error) {
      console.error('Error getting latest movies:', error);
      throw error;
    }
  }

  // Get latest TV shows
  async getLatestTVShows(options = {}) {
    try {
      const response = await tmdbClient.get('/tv/latest', {
        params: {
          language: 'en-US',
          ...options
        }
      });
      return formatMovieData(response.data);
    } catch (error) {
      console.error('Error getting latest TV shows:', error);
      throw error;
    }
  }

  // Get details for specific movie/TV show
  async getMovieDetails(movieId) {
    try {
      const response = await tmdbClient.get(`/movie/${movieId}`);
      return formatMovieData(response.data);
    } catch (error) {
      console.error('Error getting movie details:', error);
      throw error;
    }
  }

  async getTVShowDetails(tvId) {
    try {
      const response = await tmdbClient.get(`/tv/${tvId}`);
      return formatMovieData(response.data);
    } catch (error) {
      console.error('Error getting TV show details:', error);
      throw error;
    }
  }

  // Get genres
  async getMovieGenres() {
    try {
      const response = await tmdbClient.get('/genre/movie/list');
      return response.data.genres;
    } catch (error) {
      console.error('Error getting movie genres:', error);
      throw error;
    }
  }

  async getTVGenres() {
    try {
      const response = await tmdbClient.get('/genre/tv/list');
      return response.data.genres;
    } catch (error) {
      console.error('Error getting TV genres:', error);
      throw error;
    }
  }

  // Get movies/TV shows by genre
  async getMoviesByGenre(genreId, options = {}) {
    try {
      const response = await tmdbClient.get('/discover/movie', {
        params: {
          with_genres: genreId,
          language: 'en-US',
          page: options.page || 1,
          sort_by: options.sort_by || 'popularity.desc',
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting movies by genre:', error);
      throw error;
    }
  }

  async getTVShowsByGenre(genreId, options = {}) {
    try {
      const response = await tmdbClient.get('/discover/tv', {
        params: {
          with_genres: genreId,
          language: 'en-US',
          page: options.page || 1,
          sort_by: options.sort_by || 'popularity.desc',
          ...options
        }
      });
      return {
        results: response.data.results.map(formatMovieData),
        total_pages: response.data.total_pages,
        total_results: response.data.total_results,
        page: response.data.page
      };
    } catch (error) {
      console.error('Error getting TV shows by genre:', error);
      throw error;
    }
  }
}

// Request cache to prevent duplicate API calls
const requestCache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

const getCachedRequest = (key) => {
  const cached = requestCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedRequest = (key, data) => {
  requestCache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Enhanced service with caching
class CachedTMDBAPIService extends TMDBAPIService {
  async searchMulti(query, options = {}) {
    const cacheKey = `search-multi-${query}-${JSON.stringify(options)}`;
    const cached = getCachedRequest(cacheKey);
    
    if (cached) {
      console.log('Using cached search result for:', query);
      return cached;
    }
    
    try {
      const result = await super.searchMulti(query, options);
      setCachedRequest(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error searching multi:', error);
      throw error;
    }
  }

  async getLatestMovies(options = {}) {
    const cacheKey = `latest-movies-${JSON.stringify(options)}`;
    const cached = getCachedRequest(cacheKey);
    
    if (cached) {
      console.log('Using cached latest movies');
      return cached;
    }
    
    try {
      const result = await super.getLatestMovies(options);
      setCachedRequest(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error getting latest movies:', error);
      throw error;
    }
  }

  async getLatestTVShows(options = {}) {
    const cacheKey = `latest-tv-${JSON.stringify(options)}`;
    const cached = getCachedRequest(cacheKey);
    
    if (cached) {
      console.log('Using cached latest TV shows');
      return cached;
    }
    
    try {
      const result = await super.getLatestTVShows(options);
      setCachedRequest(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error getting latest TV shows:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const tmdbService = new CachedTMDBAPIService();
export default tmdbService;

// Export individual service class for testing
export { TMDBAPIService, TMDB_API_CONFIG };
